// filepath: apps/web/src/renderers/form/fields/reference/ReferencePicker.tsx
//
// FR-UI-FORM-1 AC-2 — presentational reference picker.
//
// Pairs with useReferencePicker (the data hook in this folder) to
// render a searchable dropdown of records from the target entity type.
// The component is pure: it owns only local UI state (open / closed,
// highlighted option index, search input). The hook owns the data
// fetch, debounce, and cache key.
//
// ACCESSIBILITY (FR-UI-CORE-9 / WCAG 2.1 AA):
//   - The text input carries `role="combobox"` with `aria-expanded`
//     and `aria-controls` pointing at the listbox id.
//   - The listbox carries `role="listbox"` with an id matching
//     aria-controls.
//   - The highlighted option carries `role="option"` with
//     `aria-selected="true"`; all options carry `aria-selected="false"`
//     (or unset, for the simpler 2-state pattern).
//   - `aria-activedescendant` on the input tracks the highlighted
//     option id.
//   - Keyboard nav: ArrowDown / ArrowUp / Home / End move the
//     highlight; Enter selects; Escape closes the dropdown; Tab
//     commits the typed search into `value` and closes the dropdown.
//   - The picker's chrome flows through `useTranslation().t('reference.*')`
//     — no hardcoded user-visible strings (FR-UI-CORE-8).
//   - Row labels render as React text nodes (INV-9).
//
// SECURITY:
//   - The picker never issues a per-row GET against
//     /api/entity-records/{type}/{id} — only the list endpoint, so a
//     record the server stripped in the list response stays stripped
//     in the picker (INV-2 / FR-AUTH-7).
//   - The picker's submit payload is `{record.id}` only — never the
//     display label, never the cursor. `useFormSubmit` carries the
//     optimistic-concurrency token (INV-7).
//
// This file is the registered component behind ReferenceField. The
// picker is a thin shell around ReferencePicker; see ReferenceField.tsx.

import React from "react";
import {
  useReferencePicker,
  type ReferencePickerRow,
} from "./useReferencePicker";
import { useTranslation } from "@/i18n/useTranslation";
import { useIsMobile, MIN_TOUCH_TARGET_PX } from "@/runtime/useIsMobile";

export interface ReferencePickerProps {
  /** Target entity type id (e.g. "customer"). */
  entityType: string;
  /** Field on the target entity to display in the picker (e.g. "name"). */
  displayField: string;
  /** Currently selected record id, or undefined/null when no selection. */
  value: string | null | undefined;
  /** Called with the picked record id when the user selects an option. */
  onChange: (next: string) => void;
  /** Disable the picker entirely (read-only / disabled display). */
  disabled?: boolean;
  /** Identifier for the picker DOM root (used by `aria-controls`). */
  id: string;
}

/**
 * ReferencePicker — text input + dropdown list of records.
 *
 * The component is presentational: the data hook handles fetch,
 * debounce, and cache. The component handles local UI state
 * (dropdown open / closed, highlighted option index).
 *
 * FR-UI-NFR-3 AC-4: on mobile (≤375px), the picker opens as a bottom
 * sheet with a touch-friendly Select/Cancel footer bar.
 */
export function ReferencePicker({
  entityType,
  displayField,
  value,
  onChange,
  disabled = false,
  id,
}: ReferencePickerProps): React.ReactElement {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState<string>("");
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(0);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const { data, isLoading, isFetching, error } = useReferencePicker({
    entityType,
    displayField,
    search,
    enabled: !disabled,
  });

  const records = data?.records ?? [];
  const listboxId = `${id}-listbox`;
  const getOptionId = (idx: number): string => `${id}-option-${idx}`;

  // Reset highlight whenever the records change (e.g. a new search).
  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [records]);

  // Click-outside: close the dropdown when the user clicks outside (desktop only).
  React.useEffect(() => {
    if (!isOpen || isMobile) return;
    function handlePointerDown(event: MouseEvent): void {
      const root = containerRef.current;
      if (root === null) return;
      if (event.target instanceof Node && root.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, isMobile]);

  const handleSelect = React.useCallback(
    (row: ReferencePickerRow): void => {
      onChange(row.id);
      setSearch("");
      setIsOpen(false);
    },
    [onChange],
  );

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          return;
        }
        setHighlightedIndex((idx) =>
          Math.min(idx + 1, Math.max(0, records.length - 1)),
        );
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          return;
        }
        setHighlightedIndex((idx) => Math.max(0, idx - 1));
        return;
      }
      if (event.key === "Home") {
        event.preventDefault();
        setHighlightedIndex(0);
        return;
      }
      if (event.key === "End") {
        event.preventDefault();
        setHighlightedIndex(Math.max(0, records.length - 1));
        return;
      }
      if (event.key === "Enter") {
        if (isOpen && records.length > 0) {
          event.preventDefault();
          const row = records[highlightedIndex];
          if (row !== undefined) {
            handleSelect(row);
          }
        }
        return;
      }
      if (event.key === "Escape") {
        if (isOpen) {
          event.preventDefault();
          setIsOpen(false);
        }
        return;
      }
    },
    [isOpen, records, highlightedIndex, handleSelect],
  );

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const next = event.target.value;
      setSearch(next);
      if (!isOpen) setIsOpen(true);
    },
    [isOpen],
  );

  const handleInputFocus = React.useCallback((): void => {
    if (disabled) return;
    setIsOpen(true);
  }, [disabled]);

  const openBottomSheet = React.useCallback((): void => {
    if (disabled) return;
    setIsOpen(true);
  }, [disabled]);

  const closeBottomSheet = React.useCallback((): void => {
    setIsOpen(false);
    setSearch("");
    setHighlightedIndex(0);
  }, []);

  const renderStatus = (): React.ReactNode => {
    if (error !== null) {
      return (
        <div
          className="reference-picker-status reference-picker-error"
          data-testid={`${id}-error`}
          role="status"
        >
          {t("reference.error")}
        </div>
      );
    }
    if (isLoading || (isFetching && records.length === 0)) {
      return (
        <div
          className="reference-picker-status reference-picker-loading"
          data-testid={`${id}-loading`}
          role="status"
        >
          {t("reference.loading")}
        </div>
      );
    }
    if (records.length === 0) {
      return (
        <div
          className="reference-picker-status reference-picker-empty"
          data-testid={`${id}-empty`}
          role="status"
        >
          {t("reference.empty")}
        </div>
      );
    }
    return null;
  };

  // When disabled, the dropdown never opens — even on focus. The listbox
  // never renders, so aria-activedescendant is also null.
  const activeOptionId =
    !disabled &&
    isOpen &&
    records.length > 0 &&
    records[highlightedIndex] !== undefined
      ? getOptionId(highlightedIndex)
      : undefined;

  // ─── Mobile bottom sheet ────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        {/* Trigger area: combobox input + "Choose" button */}
        <div
          ref={containerRef}
          className="reference-picker"
          data-testid={`field-${id}`}
        >
          <input
            type="text"
            id={id}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-controls={isOpen ? listboxId : undefined}
            autoComplete="off"
            spellCheck={false}
            value={search}
            onChange={handleInputChange}
            onFocus={openBottomSheet}
            onKeyDown={handleInputKeyDown}
            placeholder={t("reference.placeholder")}
            disabled={disabled}
            data-testid={`input-${id}`}
            style={{
              width: "100%",
              minHeight: `${MIN_TOUCH_TARGET_PX}px`,
              padding: "8px 12px",
              fontSize: "16px",
              boxSizing: "border-box",
            }}
          />
          {value !== undefined && value !== null && value !== "" ? (
            <div
              className="reference-picker-selected"
              data-testid={`selected-${id}`}
            >
              <span className="reference-picker-selected-id">{value}</span>
            </div>
          ) : null}
          {/* Touch-friendly "Choose..." button for discoverability */}
          <button
            type="button"
            onClick={openBottomSheet}
            disabled={disabled}
            data-testid={`choose-${id}`}
            style={{
              marginTop: "8px",
              width: "100%",
              minHeight: `${MIN_TOUCH_TARGET_PX}px`,
              fontSize: "16px",
              padding: "8px 12px",
              boxSizing: "border-box",
            }}
          >
            {t("reference.placeholder")}
          </button>
        </div>

        {/* Mobile bottom sheet overlay */}
        {isOpen ? (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t("reference.placeholder")}
            data-testid={`bottom-sheet-${id}`}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            {/* Backdrop */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
              }}
              onClick={closeBottomSheet}
              aria-hidden="true"
            />

            {/* Sheet */}
            <div
              style={{
                position: "relative",
                backgroundColor: "#fff",
                borderRadius: "var(--radius-md) var(--radius-md) 0 0",
                maxHeight: "85vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Sheet handle */}
              <div
                style={{
                  width: "36px",
                  height: "4px",
                  borderRadius: "var(--radius-md)",
                  backgroundColor: "#ddd",
                  margin: "12px auto",
                  flexShrink: 0,
                }}
                aria-hidden="true"
              />

              {/* Sheet header with close button */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 16px",
                  borderBottom: "1px solid #eee",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    flex: 1,
                    fontWeight: 600,
                    fontSize: "16px",
                  }}
                >
                  {t("reference.placeholder")}
                </span>
                <button
                  type="button"
                  onClick={closeBottomSheet}
                  aria-label={t("reference.cancel") ?? "Close"}
                  style={{
                    minWidth: `${MIN_TOUCH_TARGET_PX}px`,
                    minHeight: `${MIN_TOUCH_TARGET_PX}px`,
                    padding: "8px",
                    fontSize: "14px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Search input inside sheet */}
              <div style={{ padding: "12px 16px", flexShrink: 0 }}>
                <input
                  type="text"
                  autoFocus
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded
                  aria-controls={listboxId}
                  aria-activedescendant={activeOptionId}
                  autoComplete="off"
                  spellCheck={false}
                  value={search}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  placeholder={t("reference.placeholder")}
                  data-testid={`input-sheet-${id}`}
                  style={{
                    width: "100%",
                    minHeight: `${MIN_TOUCH_TARGET_PX}px`,
                    padding: "8px 12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    border: "1px solid #ccc",
                    borderRadius: "var(--radius-md)",
                  }}
                />
              </div>

              {/* Scrollable list */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overscrollBehavior: "contain",
                }}
              >
                <ul
                  id={listboxId}
                  role="listbox"
                  className="reference-picker-listbox"
                  data-testid={`listbox-${id}`}
                  style={{ listStyle: "none", margin: 0, padding: 0 }}
                >
                  {renderStatus()}
                  {records.map((row, idx) => {
                    const isHighlighted = idx === highlightedIndex;
                    return (
                      <li
                        key={row.id}
                        id={getOptionId(idx)}
                        role="option"
                        aria-selected={isHighlighted ? "true" : "false"}
                        className={
                          isHighlighted
                            ? "reference-picker-option reference-picker-option-active"
                            : "reference-picker-option"
                        }
                        data-testid={`option-${id}-${row.id}`}
                        onClick={() => handleSelect(row)}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                        style={{
                          padding: "12px 16px",
                          minHeight: `${MIN_TOUCH_TARGET_PX}px`,
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          backgroundColor: isHighlighted
                            ? "#f0f0f0"
                            : "transparent",
                          borderBottom: "1px solid #f5f5f5",
                          boxSizing: "border-box",
                        }}
                      >
                        <span className="reference-picker-option-label">
                          {row.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Sheet footer: Cancel + Select */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  padding: "12px 16px",
                  borderTop: "1px solid #eee",
                  flexShrink: 0,
                  paddingBottom: "max(12px, env(safe-area-inset-bottom))",
                }}
              >
                <button
                  type="button"
                  onClick={closeBottomSheet}
                  style={{
                    flex: 1,
                    minHeight: `${MIN_TOUCH_TARGET_PX}px`,
                    fontSize: "16px",
                    padding: "10px 16px",
                    backgroundColor: "#f5f5f5",
                    border: "none",
                    borderRadius: "var(--radius-md)",
                    cursor: "pointer",
                  }}
                >
                  {t("reference.cancel") ?? "Cancel"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const row = records[highlightedIndex];
                    if (row !== undefined) handleSelect(row);
                  }}
                  disabled={records.length === 0}
                  style={{
                    flex: 1,
                    minHeight: `${MIN_TOUCH_TARGET_PX}px`,
                    fontSize: "16px",
                    padding: "10px 16px",
                    backgroundColor:
                      records.length === 0 ? "#e0e0e0" : "#007bff",
                    color: records.length === 0 ? "#999" : "#fff",
                    border: "none",
                    borderRadius: "var(--radius-md)",
                    cursor: records.length === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  {t("reference.select") ?? "Select"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  // ─── Desktop dropdown (original behavior) ──────────────────────────────
  return (
    <div
      ref={containerRef}
      className="reference-picker"
      data-testid={`field-${id}`}
    >
      <input
        type="text"
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={activeOptionId}
        autoComplete="off"
        spellCheck={false}
        value={search}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleInputKeyDown}
        placeholder={t("reference.placeholder")}
        disabled={disabled}
        data-testid={`input-${id}`}
      />
      {value !== undefined && value !== null && value !== "" ? (
        <div
          className="reference-picker-selected"
          data-testid={`selected-${id}`}
        >
          <span className="reference-picker-selected-id">{value}</span>
        </div>
      ) : null}
      {isOpen ? (
        <ul
          id={listboxId}
          role="listbox"
          className="reference-picker-listbox"
          data-testid={`listbox-${id}`}
        >
          {renderStatus()}
          {records.map((row, idx) => {
            const isHighlighted = idx === highlightedIndex;
            return (
              <li
                key={row.id}
                id={getOptionId(idx)}
                role="option"
                aria-selected={isHighlighted ? "true" : "false"}
                className={
                  isHighlighted
                    ? "reference-picker-option reference-picker-option-active"
                    : "reference-picker-option"
                }
                data-testid={`option-${id}-${row.id}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSelect(row);
                }}
              >
                <span className="reference-picker-option-label">
                  {row.label}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
